import { isCarrierMark } from './constants';

// The pie segments a pedigree node paints, and the rule that gives each one its
// colour. Extracted from Canvas.jsx by TGCLOUD-1793 Phase 1 so the rule can be
// tested: Canvas is ~4,000 lines, imports d3 for real and drives a layout
// worker, so it is not mountable in jsdom, and this logic consequently had ZERO
// coverage while being the thing that decides what a clinician sees.
//
// Pure: no d3, no React, no DOM.

// A stored colour can be absent OR an empty string. `getUniqueDiagnoses` applies
// no colour filter, unlike the server allocator, and an FHQ disease can arrive
// carrying `color: ''` — see the note at
// backend/services/diagnosisColourReconciliation.js. `??` does NOT fall through
// on '', and d3 given an empty fill removes the property, which paints the
// segment SVG-default BLACK — indistinguishable from classic Bennett "affected".
// So the guard has to be emptiness, not nullishness.
export const hasColour = (c) => typeof c === 'string' && c.trim() !== '';

/**
 * Resolve one disease's colour, TGCLOUD-1504 remediation B4.
 *
 * The canonical family map wins; the member's own stored copy is the fallback.
 * Before this, the node fill read `elem.color` (each member's private copy)
 * while the legend swatch read the de-duplicated family map — so when two
 * members disagreed, the legend said one colour and the node painted another,
 * with nothing on screen to say so. That asymmetry WAS TGCLOUD-1504 and it was
 * only ever closed by making the database agree, never at render time.
 */
export const resolveDiseaseColour = (
  diseaseId,
  storedColour,
  diseaseColorMap
) => {
  const canonical = (diseaseColorMap || {})[diseaseId];
  if (canonical && hasColour(canonical.color)) return canonical.color;
  return storedColour;
};

/**
 * Build the segment list for one member's symbol.
 *
 * @param {object[]} diagnoses        the member's own `diagnoses` array
 * @param {object}   memberStatuses   per-disease carrier status for this member
 * @param {object}   diseaseColorMap  canonical disease id -> { color, name }
 * @param {object}   labelsSet        the EFFECTIVE label settings (diseases, diseasesMap)
 * @param {boolean}  showCarrierMarks whether carrier segments are drawn
 */
export function buildDiagnoseSegments({
  diagnoses = [],
  memberStatuses = {},
  diseaseColorMap = {},
  labelsSet = {},
  showCarrierMarks = true
} = {}) {
  const visible = (diagnoses || []).filter(
    (elem) =>
      elem &&
      elem.displayOnPedigree === true &&
      labelsSet.diseases &&
      labelsSet.diseasesMap?.[elem.id] &&
      // FEATURE_CARRIER_STATUS_IN_CONDITION: an explicitly unaffected / unknown
      // condition is shown but NOT affected, so it must not paint disease
      // shading (a carrier still does, via its carrier status). Legacy data has
      // no affectedStatus, so this is inert flag-off.
      elem.affectedStatus !== 'unaffected' &&
      elem.affectedStatus !== 'unknown'
  );

  // One segment per disease. A clinically-confirmed record wins the slot,
  // because the confirmed stripe is drawn from this entry.
  const unique = visible.reduce((acc, current) => {
    const existingIndex = acc.findIndex((item) => item.id === current.id);
    if (existingIndex === -1) acc.push(current);
    else if (current.clinicallyConfirmed) acc[existingIndex] = current;
    return acc;
  }, []);

  const segments = unique.map((elem) => {
    const cs = memberStatuses[elem.id];
    return {
      id: elem.id,
      color: resolveDiseaseColour(elem.id, elem.color, diseaseColorMap),
      clinicallyConfirmed: elem.clinicallyConfirmed,
      status: isCarrierMark(cs) ? cs : 'affected'
    };
  });

  // Carrier segments: diseases this member carries (tested/obligate) but is not
  // affected by. A carrier has no local diagnosis for the disease, so the colour
  // can only come from the family map — there is no member copy to fall back to.
  if (showCarrierMarks && labelsSet.diseases) {
    const present = new Set(segments.map((s) => s.id));
    Object.keys(memberStatuses).forEach((diseaseId) => {
      const st = memberStatuses[diseaseId];
      if (
        isCarrierMark(st) &&
        !present.has(diseaseId) &&
        labelsSet.diseasesMap?.[diseaseId]
      ) {
        const canonical = (diseaseColorMap || {})[diseaseId];
        segments.push({
          id: diseaseId,
          color: (canonical && canonical.color) || '#000',
          clinicallyConfirmed: false,
          status: st
        });
      }
    });
  }

  return segments;
}
